import { LANGUAGE_CODE } from './environment.js';
export const number = (value, decimals = 0, isCompact = true, showSign = 'auto') => new Intl.NumberFormat(LANGUAGE_CODE, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    notation: isCompact ? 'compact' : 'standard',
    signDisplay: showSign,
    style: 'decimal',
}).format(value || 0);
export const integer = (value, isCompact = true, showSign = 'auto') => new Intl.NumberFormat(LANGUAGE_CODE, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    notation: isCompact ? 'compact' : 'standard',
    signDisplay: showSign,
    style: 'decimal',
}).format(value || 0);
export const percentage = (value, decimals = 2, showSign = 'auto') => new Intl.NumberFormat(LANGUAGE_CODE, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    signDisplay: showSign,
    style: 'percent',
}).format(value || 0);
export const currency = (value, currencyCode = 'EUR', isCompact = true) => new Intl.NumberFormat(LANGUAGE_CODE, {
    currency: currencyCode,
    notation: isCompact ? 'compact' : 'standard',
    style: 'currency',
}).format(value || 0);
export const time = (value) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    hour: 'numeric',
    minute: 'numeric',
}).format(new Date(value));
export const dateShort = (value) => {
    const OPTIONS = {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    };
    return new Intl.DateTimeFormat(LANGUAGE_CODE, OPTIONS).format(new Date(value));
};
export const dateLong = (value) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
    year: 'numeric',
}).format(new Date(value));
export const dateTimeShort = (value) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    month: 'numeric',
    year: 'numeric',
}).format(new Date(value));
export const dateTimeLong = (value) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    month: 'short',
    weekday: 'short',
    year: 'numeric',
}).format(new Date(value));
export const timeRange = (valueA, valueB) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    hour: 'numeric',
    minute: 'numeric',
}).formatRange(valueA, valueB);
export const dateRange = (valueA, valueB) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
    year: 'numeric',
}).formatRange(valueA, valueB);
export const dateTimeRange = (valueA, valueB) => new Intl.DateTimeFormat(LANGUAGE_CODE, {
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    month: 'short',
    year: 'numeric',
}).formatRange(valueA, valueB);
const MILLESECONDS_PER_SECOND = 1000;
const MILLESECONDS_PER_MINUTE = 60 * MILLESECONDS_PER_SECOND;
const MILLESECONDS_PER_HOUR = 60 * MILLESECONDS_PER_MINUTE;
const MILLESECONDS_PER_DAY = 24 * MILLESECONDS_PER_HOUR;
const MILLESECONDS_PER_WEEK = 7 * MILLESECONDS_PER_DAY;
const MILLESECONDS_PER_MONTH = 30 * MILLESECONDS_PER_DAY;
const MILLESECONDS_PER_YEAR = 365 * MILLESECONDS_PER_DAY;
export const weeksToDuration = (weeks) => {
    if (weeks === 1) {
        return `${weeks} week`;
    }
    if (weeks > 1) {
        return `${weeks} weeks`;
    }
    return '';
};
export const daysToDuration = (days) => {
    const WEEKS = Math.floor(Math.abs(days) / 7);
    const DAYS = Math.abs(days) % 7;
    if (DAYS === 0) {
        return weeksToDuration(WEEKS);
    }
    if (DAYS === 1) {
        return ` ${weeksToDuration(WEEKS)} ${DAYS} day`;
    }
    if (DAYS > 1) {
        return ` ${weeksToDuration(WEEKS)} ${DAYS} days`;
    }
    return '';
};
// eslint-disable-next-line max-statements, max-lines-per-function, complexity
export const relativeTime = (date) => {
    const FORMATTER = new Intl.RelativeTimeFormat('en');
    const DELTA = new Date(date).getTime() - new Date().getTime();
    const THRESHOLD = 2;
    const YEARS = Math.floor(DELTA / MILLESECONDS_PER_YEAR);
    const MONTHS = Math.floor(DELTA / MILLESECONDS_PER_MONTH);
    const WEEKS = Math.floor(DELTA / MILLESECONDS_PER_WEEK);
    const DAYS = Math.floor(DELTA / MILLESECONDS_PER_DAY);
    const HOURS = Math.floor(DELTA / MILLESECONDS_PER_HOUR);
    const MINUTES = Math.floor(DELTA / MILLESECONDS_PER_MINUTE);
    const SECONDS = Math.floor(DELTA / MILLESECONDS_PER_SECOND);
    if (Math.abs(YEARS) > THRESHOLD) {
        return FORMATTER.format(YEARS, 'year');
    }
    if (Math.abs(MONTHS) > THRESHOLD) {
        return FORMATTER.format(MONTHS, 'month');
    }
    if (Math.abs(WEEKS) > THRESHOLD) {
        return FORMATTER.format(WEEKS, 'week');
    }
    if (Math.abs(DAYS) > THRESHOLD) {
        return FORMATTER.format(DAYS, 'day');
    }
    if (Math.abs(HOURS) > THRESHOLD) {
        return FORMATTER.format(HOURS, 'hour');
    }
    if (Math.abs(MINUTES) > THRESHOLD) {
        return FORMATTER.format(MINUTES, 'minute');
    }
    if (Math.abs(SECONDS) > THRESHOLD) {
        return FORMATTER.format(SECONDS, 'second');
    }
    return '';
};
export const mobile = (code, number) => `+${code} (0)${number}`;
export const initials = (name) => {
    if (name === undefined) {
        return '';
    }
    return [(name.first ?? '').split('-'), (name.last ?? '').split('-')]
        .flat()
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase();
};
